/**
 * The Peon of the Month card's header (docs/bounties.md "The crown is last month's"): the crowned
 * portrait, the name and "N tasks completed in August" belong to the month that has finished; the
 * standings under the divider stay this month's race. Pure words and choice, shared by the web and
 * the native app so the two cards can never disagree.
 */
import type { Crown, TopPeon } from "./types";

export interface CrownHeader {
  /** Who the header names and whose shirt the crowned portrait wears. */
  peon: TopPeon;
  /** Which month they won: the one before this, or this one so far. */
  month: Crown["month"];
  /** That month's name on this device, "August". */
  monthName: string;
  /** "14 tasks completed in August", "1 task completed in August". */
  countLine: string;
}

/** The device's own name for the month `offset` months from `now`'s: 0 this month, -1 the one before (December for a January card). */
export function monthNameOf(now: Date, offset: number, locale?: string): string {
  return new Date(now.getFullYear(), now.getMonth() + offset, 1).toLocaleString(locale, { month: "long" });
}

/** "4 tasks completed in September". */
export function crownCount(count: number, monthName: string): string {
  return `${count} task${count === 1 ? "" : "s"} completed in ${monthName}`;
}

/** The header when nobody has a crown to wear, last month or this: this month is up for grabs. */
export function emptyCrownLine(now: Date = new Date(), locale?: string): string {
  return `No tasks completed yet in ${monthNameOf(now, 0, locale)}. The crown is up for grabs.`;
}

/**
 * What the header draws, from the summary's `crown` (or `top` from a summary without one, read as
 * this month's leader), or null when nobody is crowned and the header shows emptyCrownLine.
 */
export function crownHeader(
  summary: { crown?: Crown | null; top?: TopPeon | null } | null | undefined,
  now: Date = new Date(),
  locale?: string,
): CrownHeader | null {
  if (!summary) return null;
  const crown: Crown | null = summary.crown !== undefined ? summary.crown : summary.top ? { peon: summary.top, month: "current" } : null;
  if (!crown) return null;
  const monthName = monthNameOf(now, crown.month === "previous" ? -1 : 0, locale);
  return { peon: crown.peon, month: crown.month, monthName, countLine: crownCount(crown.peon.count, monthName) };
}
